import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  getAuth,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';

const MIN_PASSWORD_LENGTH = 8;

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
}));

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)',
  border: '1px solid rgba(0,0,0,0.06)',
  width: '100%',
  maxWidth: 480,
  overflow: 'hidden',
}));

const CardHeader = styled(Box)({
  background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
  padding: '28px 32px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

const CardBody = styled(Box)({
  padding: '32px',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#FAFAFA',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: '#C7D2FE' },
    '&.Mui-focused fieldset': { borderColor: '#4F46E5', borderWidth: 2 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4F46E5' },
});

const PrimaryButton = styled(Button)({
  backgroundColor: '#4F46E5',
  color: '#ffffff',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(79, 70, 229, 0.3)',
  '&:hover': {
    backgroundColor: '#4338CA',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.4)',
  },
  '&:disabled': {
    backgroundColor: '#A5B4FC',
    boxShadow: 'none',
    color: '#ffffff',
  },
});

const SecondaryButton = styled(Button)({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '15px',
  fontWeight: 500,
  textTransform: 'none',
  color: '#6B7280',
  borderColor: '#E5E7EB',
  '&:hover': {
    borderColor: '#C7D2FE',
    backgroundColor: '#F5F3FF',
    color: '#4F46E5',
  },
});

const RequirementRow = styled(Box)(({ met }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  color: met ? '#059669' : '#9CA3AF',
  fontSize: '13px',
  transition: 'color 0.2s',
}));

export default function ChangePassword() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const meetsLength = newPassword.length >= MIN_PASSWORD_LENGTH;
  const matchesConfirm = newPassword.length > 0 && newPassword === confirmPassword;
  const isDifferent = newPassword.length > 0 && newPassword !== currentPassword;

  const getFriendlyError = (code) => {
    switch (code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Your current password is incorrect. Please try again.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'auth/requires-recent-login':
        return 'For security, please log out and log back in before changing your password.';
      case 'auth/weak-password':
        return 'That password is too weak. Please choose a stronger one.';
      default:
        return 'Something went wrong. Please try again or contact support.';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) return setError('Please enter your current password.');
    if (!meetsLength) return setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
    if (!isDifferent) return setError('New password must be different from your current password.');
    if (!matchesConfirm) return setError('New passwords do not match.');

    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (err) {
      setError(getFriendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const eyeToggle = (visible, setVisible) => (
    <InputAdornment position="end">
      <IconButton onClick={() => setVisible(!visible)} edge="end" size="small"
        sx={{ color: '#9CA3AF', '&:hover': { color: '#4F46E5' } }}>
        {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <PageWrapper>
      <Card>
        <CardHeader>
          <Box sx={{
            width: 44, height: 44, borderRadius: '10px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <LockResetOutlinedIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
              Change Password
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
              Update your account password
            </Typography>
          </Box>
        </CardHeader>

        <CardBody>
          {success ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#059669', mb: 2 }} />
              <Typography variant="h6" fontWeight={600} color="#111827" gutterBottom>
                Password updated
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your password has been changed successfully. Redirecting you to the dashboard…
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                  {error}
                </Alert>
              )}

              <StyledTextField
                fullWidth
                label="Current Password"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setError(''); }}
                required
                sx={{ mb: 3 }}
                InputProps={{ endAdornment: eyeToggle(showCurrent, setShowCurrent) }}
              />

              <Divider sx={{ mb: 3, borderStyle: 'dashed' }} />

              <StyledTextField
                fullWidth
                label="New Password"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                required
                sx={{ mb: 2 }}
                InputProps={{ endAdornment: eyeToggle(showNew, setShowNew) }}
              />

              {/* Live requirements */}
              <Box sx={{ mb: 3, pl: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <RequirementRow met={meetsLength ? 1 : 0}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                  At least {MIN_PASSWORD_LENGTH} characters
                </RequirementRow>
                <RequirementRow met={isDifferent ? 1 : 0}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                  Different from current password
                </RequirementRow>
              </Box>

              <StyledTextField
                fullWidth
                label="Confirm New Password"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                required
                sx={{ mb: 1 }}
                InputProps={{ endAdornment: eyeToggle(showConfirm, setShowConfirm) }}
              />

              {confirmPassword.length > 0 && (
                <Box sx={{ mb: 3, pl: 0.5 }}>
                  <RequirementRow met={matchesConfirm ? 1 : 0}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                    Passwords match
                  </RequirementRow>
                </Box>
              )}
              {confirmPassword.length === 0 && <Box sx={{ mb: 3 }} />}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <PrimaryButton
                  type="submit"
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  {loading
                    ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />Updating…</>
                    : 'Update Password'}
                </PrimaryButton>
                <SecondaryButton
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </SecondaryButton>
              </Box>
            </Box>
          )}
        </CardBody>
      </Card>
    </PageWrapper>
  );
}
